import { lazy, Suspense } from "react";

import { Route, Routes } from "react-router-dom";

import AuthActionHandler from "../components/auth/AuthActionHandler";
import GodRoute from "../components/common/GodRoute";
import Hourglass from "../components/common/Hourglass";
import PrivateRoute from "../components/common/PrivateRoute";
import { EVENTS_PAGE_TABS } from "../constants/eventsPageTabs";
import { NOTICES_PAGE_TABS } from "../constants/noticesPageTabs";
import { useData } from "../contextProviders/DataProvider";

// Eagerly loaded public/lightweight pages
import AboutPage from "../pages/AboutPage";
import AccountEmailVerifiedPage from "../pages/AccountEmailVerifiedPage";
import AccountForgotPasswordPage from "../pages/AccountForgotPasswordPage";
import AccountRecoverEmailPage from "../pages/AccountRecoverEmailPage";
import AccountResetPasswordPage from "../pages/AccountResetPasswordPage";
import AccountVerifyEmailPage from "../pages/AccountVerifyEmailPage";
import CopyrightPage from "../pages/CopyrightPage";
import LoadingErrorPage from "../pages/LoadingErrorPage";
import LoginPage from "../pages/LoginPage";
import LogoutPage from "../pages/LogoutPage";
import WelcomePage from "../pages/WelcomePage";

// Lazy-loaded member-facing pages
const AcceptOrgInvitationPage = lazy(() => import("../pages/AcceptOrgInvitationPage"));
const AccountPage = lazy(() => import("../pages/AccountPage"));
const AccountVerifyEmailChangePage = lazy(() => import("../pages/AccountVerifyEmailChangePage"));
const ApplyPage = lazy(() => import("../pages/ApplyPage"));
const CoursePage = lazy(() => import("../pages/CoursePage"));
const CoursesPage = lazy(() => import("../pages/CoursesPage"));
const EventPage = lazy(() => import("../pages/EventPage"));
const EventResultsPage = lazy(() => import("../pages/EventResultsPage"));
const EventsPage = lazy(() => import("../pages/EventsPage"));
const GuestsPage = lazy(() => import("../pages/GuestsPage"));
const HandbookPage = lazy(() => import("../pages/HandbookPage"));
const HandbooksPage = lazy(() => import("../pages/HandbooksPage"));
const HomePage = lazy(() => import("../pages/HomePage"));
const LeaguePage = lazy(() => import("../pages/LeaguePage"));
const LeaguesPage = lazy(() => import("../pages/LeaguesPage"));
const MembersPage = lazy(() => import("../pages/MembersPage"));
const NoticePage = lazy(() => import("../pages/NoticePage"));
const NoticesPage = lazy(() => import("../pages/NoticesPage"));
const OrgPage = lazy(() => import("../pages/OrgPage"));
const OrgsPage = lazy(() => import("../pages/OrgsPage"));
const PersonPage = lazy(() => import("../pages/PersonPage"));
const RegFormPage = lazy(() => import("../pages/RegFormPage"));
const SeasonPage = lazy(() => import("../pages/SeasonPage"));
const SeasonPairingsPage = lazy(() => import("../pages/SeasonPairingsPage"));
const SeasonsPage = lazy(() => import("../pages/SeasonsPage"));
const TeamPage = lazy(() => import("../pages/TeamPage"));

// Lazy-loaded public pages
const PublicOrgPage = lazy(() => import("../pages/PublicOrgPage"));

// Lazy-loaded legal pages
const PrivacyPage = lazy(() => import("../pages/PrivacyPage"));
const UsgaPage = lazy(() => import("../pages/UsgaPage"));
const TermsPage = lazy(() => import("../pages/TermsPage"));

// Lazy-loaded leagueAdmin-only pages
const DivisionCreatePage = lazy(() => import("../pages/DivisionCreatePage"));
const DivisionEditPage = lazy(() => import("../pages/DivisionEditPage"));
const EventCreatePage = lazy(() => import("../pages/EventCreatePage"));
const TeamCreatePage = lazy(() => import("../pages/TeamCreatePage"));

// Lazy-loaded orgAdmin-only pages
const HandbookCreatePage = lazy(() => import("../pages/HandbookCreatePage"));
const LeagueCreatePage = lazy(() => import("../pages/LeagueCreatePage"));
const NoticeCreatePage = lazy(() => import("../pages/NoticeCreatePage"));
const DeleteLeaguePage = lazy(() => import("../pages/DeleteLeaguePage"));
const SeasonCreatePage = lazy(() => import("../pages/SeasonCreatePage"));

// Lazy-loaded bulk data upload pages
const EventUploadPage = lazy(() => import("../pages/EventUploadPage"));
const OrgMemberUploadPage = lazy(() => import("../pages/OrgMemberUploadPage"));

// Lazy-loaded God-only pages
const ApplicationRequestsPage = lazy(() => import("../pages/ApplicationRequestsPage"));
const OrgVanityUrlRequestsPage = lazy(() => import("../pages/OrgVanityUrlRequestsPage"));
const CompetitionCreatePage = lazy(() => import("../pages/CompetitionCreatePage"));
const CompetitionEditPage = lazy(() => import("../pages/CompetitionEditPage"));
const CompetitionLibraryPage = lazy(() => import("../pages/CompetitionLibraryPage"));
const CourseCreatePage = lazy(() => import("../pages/CourseCreatePage"));
const CourseImportPage = lazy(() => import("../pages/CourseImportPage"));
const DevOutboxPage = lazy(() => import("../pages/DevOutboxPage"));
const EmailTemplatesPage = lazy(() => import("../pages/EmailTemplatesPage"));
const FirestoreUtilityPage = lazy(() => import("../pages/FirestoreUtilityPage"));
const GhinUpdatePage = lazy(() => import("../pages/GhinUpdatePage"));
const GuidanceActionCreatorPage = lazy(() => import("../pages/GuidanceActionCreatorPage"));
const OrgCreatePage = lazy(() => import("../pages/OrgCreatePage"));
const UserSupportPage = lazy(() => import("../pages/UserSupportPage"));
const USGATestPage = lazy(() => import("../pages/USGATestPage"));
export default function RouteTable() {

  const { getAllActiveOrgs } = useData();

    // Handle the case where a user is only in one org - by just routing to the
  // page that shows users for just their org.
  function isOnlyOneOrg() {
    const orgs = getAllActiveOrgs();
    return orgs.length === 1;
  }

  return (
    <Suspense fallback={<Hourglass />}>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/auth-action/*" element={<AuthActionHandler />} />
        <Route path="/accept-org-invitation/:appId?/:orgName?/:email?/:name?/:invitationId?/:secret?/*" element={<AcceptOrgInvitationPage />}/>
        <Route path="/account/:personId?" element={<PrivateRoute><AccountPage /></PrivateRoute>}/>
        <Route path="/account-email-verified" element={<AccountEmailVerifiedPage />} />
        <Route path="/account-forgot-password?/*" element={<AccountForgotPasswordPage />} />
        <Route path="/account-recover-email?/*" element={<AccountRecoverEmailPage />} />
        <Route path="/account-reset-password?/*" element={<AccountResetPasswordPage />} />
        <Route path="/account-verify-email?/*" element={<AccountVerifyEmailPage />} />
        <Route path="/account-verify-email-change/:newEmail?/*" element={<PrivateRoute><AccountVerifyEmailChangePage /></PrivateRoute>} />
        <Route path="/apply/*" element={<ApplyPage />} />
        <Route path="/course/:courseId" element={<PrivateRoute><CoursePage /></PrivateRoute>}/>
        <Route path="/courses" element={<PrivateRoute><CoursesPage /></PrivateRoute>}/>
        <Route path="/course-edit/:courseId" element={<PrivateRoute><CoursePage isEditMode={true} /></PrivateRoute>}/>
        <Route path="/copyright" element={<CopyrightPage />}/>
        <Route path="/now-events" element={<PrivateRoute><EventsPage initialTab={EVENTS_PAGE_TABS.NOW} /></PrivateRoute>}/>
        <Route path="/upcoming-events" element={<PrivateRoute><EventsPage initialTab={EVENTS_PAGE_TABS.UPCOMING} /></PrivateRoute>}/>
        <Route path="/past-events" element={<PrivateRoute><EventsPage initialTab={EVENTS_PAGE_TABS.PAST} /></PrivateRoute>}/>
        <Route path="/regform/:orgId/:eventId" element={<PrivateRoute><RegFormPage /></PrivateRoute>}/>
        <Route path="/event/:orgId/:eventId" element={<PrivateRoute><EventPage /></PrivateRoute>}/>
        <Route path="/event/:orgId/:eventId/attendees" element={<PrivateRoute><EventPage initialTab='attendees' /></PrivateRoute>}/>
        <Route path="/event/:orgId/:eventId/teesheet" element={<PrivateRoute><EventPage initialTab='teesheet' /></PrivateRoute>}/>
        <Route path="/event/:orgId/:eventId/games" element={<PrivateRoute><EventPage initialTab='games' /></PrivateRoute>}/>
        <Route path="/event/:orgId/:eventId/results" element={<PrivateRoute><EventPage initialTab='results' /></PrivateRoute>}/>
        <Route path="/event/:orgId/:eventId/scores" element={<PrivateRoute><EventPage initialTab='results' /></PrivateRoute>}/>
        <Route path="/event-edit/:orgId/:eventId" element={<PrivateRoute><EventPage isEditMode={true} /></PrivateRoute>}/>
        <Route path="/event-results/:orgId/:eventId" element={<PrivateRoute><EventResultsPage isEditMode={false} /></PrivateRoute>}/>
        <Route path="/event-results-edit/:orgId/:eventId" element={<PrivateRoute><EventResultsPage isEditMode={true} /></PrivateRoute>}/>
        <Route path="/attendees-edit" element={<PrivateRoute><EventPage /></PrivateRoute>}/>
        <Route path="/events" element={<PrivateRoute><EventsPage /></PrivateRoute>}/>
        <Route path="/guests" element={<PrivateRoute><GuestsPage /></PrivateRoute>}/>
        <Route path="/handbook/:orgId/:handbookId" element={<PrivateRoute><HandbookPage /></PrivateRoute>}/>
        <Route path="/handbook-edit/:orgId/:handbookId" element={<PrivateRoute><HandbookPage isEditMode={true} /></PrivateRoute>}/>
        <Route path="/handbooks" element={<PrivateRoute><HandbooksPage /></PrivateRoute>}/>
        <Route path="/home" element={<HomePage />} />
        <Route path="/loadingerror" element={<LoadingErrorPage />} />
        <Route path="/login/*" element={<LoginPage />} />
        <Route path="/logout" element={<LogoutPage />} />
        <Route path="/league/:orgId/:leagueId" element={<PrivateRoute><LeaguePage isEditMode={false} /></PrivateRoute>}/>
        <Route path="/league/:orgId/:leagueId/results" element={<PrivateRoute><LeaguePage isEditMode={false} initialTab="2" /></PrivateRoute>}/>
        <Route path="/league-delete/:orgId/:leagueId" element={<PrivateRoute><DeleteLeaguePage /></PrivateRoute>}/>
        <Route path="/league-edit/:orgId/:leagueId" element={<PrivateRoute><LeaguePage isEditMode={true} /></PrivateRoute>}/>
        <Route path="/league-teams/:orgId/:leagueId" element={<PrivateRoute><LeaguePage isEditMode={false} initialTab="4" /></PrivateRoute>}/>
        <Route path="/leagues" element={<PrivateRoute><LeaguesPage /></PrivateRoute>}/>
        <Route path="/members" element={<PrivateRoute conditionForAltChildren={isOnlyOneOrg} altChildren={<OrgPage isEditMode={false} initialTab={'2'} orgId={getAllActiveOrgs()[0]} />}><MembersPage /></PrivateRoute>}/>
        <Route path="/notices" element={<PrivateRoute><NoticesPage /></PrivateRoute>}/>
        <Route path="/active-notices" element={<PrivateRoute><NoticesPage initialTab={NOTICES_PAGE_TABS.ACTIVE} /></PrivateRoute>}/>
        <Route path="/upcoming-notices" element={<PrivateRoute><NoticesPage initialTab={NOTICES_PAGE_TABS.UPCOMING} /></PrivateRoute>}/>
        <Route path="/past-notices" element={<PrivateRoute><NoticesPage initialTab={NOTICES_PAGE_TABS.PAST} /></PrivateRoute>}/>
        <Route path="/notice/:orgId/:noticeId" element={<PrivateRoute><NoticePage /></PrivateRoute>}/>
        <Route path="/notice-edit/:orgId/:noticeId" element={<PrivateRoute><NoticePage isEditMode={true} /></PrivateRoute>}/>
        <Route path="/orgs" element={<PrivateRoute><OrgsPage /></PrivateRoute>}/>
        <Route path="/org/:orgId" element={<PrivateRoute><OrgPage isEditMode={false} initialTab={'1'} /></PrivateRoute>}/>
        <Route path="/org-leagues/:orgId" element={<PrivateRoute><OrgPage isEditMode={false} initialTab={'1'} /></PrivateRoute>}/>
        <Route path="/org-members/:orgId" element={<PrivateRoute><OrgPage isEditMode={false} initialTab={'2'} /></PrivateRoute>}/>
        <Route path="/org-admins/:orgId" element={<PrivateRoute><OrgPage isEditMode={false} initialTab={'2'} /></PrivateRoute>}/>
        <Route path="/org-handbooks/:orgId" element={<PrivateRoute><HandbooksPage /></PrivateRoute>}/>
        <Route path="/org-edit/:orgId" element={<PrivateRoute><OrgPage isEditMode={true} initialTab={'1'} /></PrivateRoute>}/>
        <Route path="/org-invitations/:orgId" element={<PrivateRoute><OrgPage isEditMode={false} initialTab={'2'} /></PrivateRoute>}/>
        <Route path="/org-pros/:orgId" element={<PrivateRoute><OrgPage isEditMode={false} initialTab={'2'} /></PrivateRoute>}/>
        <Route path="/public/:orgId" element={<PublicOrgPage />}/>
        <Route path="/pages/:vanityUrl" element={<PublicOrgPage />}/>
        <Route path="/privacy" element={<PrivacyPage />}/>
        <Route path="/usga" element={<UsgaPage />}/>
        <Route path="/profile" element={<PrivateRoute><PersonPage isEditMode={false} /></PrivateRoute>}/>
        <Route path="/profile/:personId" element={<PrivateRoute><PersonPage isEditMode={false} /></PrivateRoute>}/>
        <Route path="/profile-edit/:personId" element={<PrivateRoute><PersonPage isEditMode={true} /></PrivateRoute>}/>
        <Route path="/season/:orgId/:seasonId" element={<PrivateRoute><SeasonPage /></PrivateRoute>}/>
        <Route path="/season-edit/:orgId/:seasonId" element={<PrivateRoute><SeasonPage isEditMode={true} /></PrivateRoute>}/>
        <Route path="/seasons" element={<PrivateRoute><SeasonsPage /></PrivateRoute>}/>
        <Route path="/season-pairings/:orgId/:seasonId" element={<PrivateRoute><SeasonPairingsPage /></PrivateRoute>}/>
        <Route path="/team/:orgId/:teamId" element={<PrivateRoute><TeamPage /></PrivateRoute>}/>
        <Route path="/terms" element={<TermsPage />}/>
        <Route path="/welcome" element={<WelcomePage />} />
        {/* League admin only routes */}
        <Route path="/division-create/:orgId/:leagueId" element={<PrivateRoute><DivisionCreatePage /></PrivateRoute>}/>
        <Route path="/division-edit/:orgId/:leagueId/:divisionId" element={<PrivateRoute><DivisionEditPage /></PrivateRoute>}/>
        <Route path="/event-create/:orgId?/:leagueId?/:seasonId?" element={<PrivateRoute><EventCreatePage /></PrivateRoute>}/>
        <Route path="/team-create/:orgId/:leagueId" element={<PrivateRoute><TeamCreatePage /></PrivateRoute>}/>
        {/* Org admin only routes */}
        <Route path="/handbook-create/:orgId?" element={<PrivateRoute><HandbookCreatePage /></PrivateRoute>}/>
        <Route path="/league-create/:orgId?" element={<PrivateRoute><LeagueCreatePage /></PrivateRoute>}/>
        <Route path="/notice-create/:orgId?" element={<PrivateRoute><NoticeCreatePage /></PrivateRoute>}/>
        <Route path="/season-create/:orgId/:leagueId" element={<PrivateRoute><SeasonCreatePage /></PrivateRoute>}/>
        <Route path="/event-uploader/:leagueId?" element={<PrivateRoute><EventUploadPage /></PrivateRoute>}/>
        <Route path="/member-uploader/:orgId?" element={<PrivateRoute><OrgMemberUploadPage /></PrivateRoute>}/>
        {/* God only routes */}
        <Route path="/competition-library" element={<GodRoute><CompetitionLibraryPage /></GodRoute>}/>
        <Route path="/competition-create" element={<GodRoute><CompetitionCreatePage /></GodRoute>}/>
        <Route path="/competition/:competitionId" element={<GodRoute><CompetitionEditPage /></GodRoute>}/>
        <Route path="/course-create" element={<GodRoute><CourseCreatePage /></GodRoute>}/>
        <Route path="/course-import" element={<GodRoute><CourseImportPage /></GodRoute>}/>
        <Route path="/email-templates" element={<GodRoute><EmailTemplatesPage /></GodRoute>}/>
        <Route path="/dev-outbox" element={<GodRoute><DevOutboxPage /></GodRoute>}/>
        <Route path="/daily-ghin-uploader" element={<GodRoute><GhinUpdatePage /></GodRoute>}/>
        <Route path="/org-create" element={<GodRoute><OrgCreatePage /></GodRoute>}/>
        <Route path="/firestore-utility" element={<GodRoute><FirestoreUtilityPage /></GodRoute>}/>
        <Route path="/requests" element={<GodRoute><ApplicationRequestsPage /></GodRoute>}/>
        <Route path="/org-vanity-url-requests" element={<GodRoute><OrgVanityUrlRequestsPage /></GodRoute>}/>
        <Route path="/user-support" element={<GodRoute><UserSupportPage /></GodRoute>}/>
        <Route path="/guidance-action-creator" element={<GodRoute><GuidanceActionCreatorPage /></GodRoute>}/>
        <Route path="/usga-test" element={<GodRoute><USGATestPage /></GodRoute>}/>
      </Routes>
    </Suspense>
  )
}